import React, {useCallback, useMemo, useRef, useState} from 'react'
import Lottie from 'lottie-react'
import styled from 'styled-components'
import {
  GIFT_SHOP_WHOLESALE_DISCOUNT_INCREMENT,
  GIFT_SHOP_WHOLESALE_MAX_CONTENT,
  GIFT_SHOP_WHOLESALE_MAX_DISCOUNT,
  GIFT_SHOP_WHOLESALE_SIM_SAMPLE_PRICE,
} from 'consts'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {GiftShopWholesaleTierData} from 'types'
import {
  formatCurrency,
  getFontFamily,
  getFontSize,
  getHowItWorksWebUrl,
  showSnackbar,
  useDidMount,
} from 'utils'
import {useTheme} from 'themes'
import {
  Button,
  Icon,
  ListFailed,
  Paragraph,
  ParsedText,
} from 'common/components'
import convertUnit from 'lib/unit'
import {useSelector} from 'lib/redux'
import animationData from '../../../../../assets/lottie/activity_indicator.json'
import {GiftShopSettingWholesaleItem} from '../WholesaleItem'
import {GiftShopSettingWholesaleContentProps} from './GiftShopSettingWholesaleContentProps'

interface StyledHeaderItemProps {
  disabled?: boolean
}

const StyledDisclaimerContainer = styled.div`
  margin: ${convertUnit(20)} 0;
`

const StyledButton = styled(Button)`
  width: 100%;
`

const StyledSectionHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  border-bottom: 1px solid ${({theme}) => theme.gray_1};
  min-height: ${convertUnit(64)};
  padding: 0 ${convertUnit(20)};
  gap: ${convertUnit(20)};
`

const StyledSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: ${({theme}) => theme.white_1};
`

const StyledLoadingContainer = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledLottie = styled(Lottie)`
  height: ${convertUnit(100)};
  width: ${convertUnit(100)};
`

const StyledContainer = styled.div`
  padding: ${convertUnit(20)};
  flex-grow: 1;
  overflow-y: scroll;
  scrollbar-width: auto;
  scrollbar-color: ${({theme}) => `${theme.primary_5} ${theme.white_3}`};

  ::-webkit-scrollbar {
    background-color: ${({theme}) => theme.white_3};
    width: ${convertUnit(25)};
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${({theme}) => theme.primary_5};
    background-clip: content-box;
    border: ${convertUnit(8)} solid ${({theme}) => theme.white_3};
    border-radius: ${convertUnit(16)};
  }
`
const StyledSimContainer = styled.div`
  padding-top: ${convertUnit(12)};
`
const StyledSimResult = styled(Paragraph)`
  padding: ${convertUnit(4)} 0;
`
const StyledSimCalc = styled(Paragraph)`
  font-size: ${getFontSize('m')};
`
const StyledTd = styled.td`
  padding: 0 ${convertUnit(8)};
`
const StyledSimCalcPrice = styled(StyledSimCalc)`
  text-align: right;
`
const StyledHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${convertUnit(12)};
`
const StyledHeaderItem = styled.div<StyledHeaderItemProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: ${({disabled}) => (disabled ? 'default' : 'pointer')};
`
const StyledLink = styled.a`
  text-decoration: none;
`

export default function GiftShopSettingWholesaleContent({
  showTitle,
  containerStyle,
}: GiftShopSettingWholesaleContentProps) {
  const {translate} = useTranslation()
  const [loading, setLoading] = useState(true)
  const [buttonLoading, setButtonLoading] = useState(false)
  const prevDatas = useRef<GiftShopWholesaleTierData[]>()
  const stateData = useState<GiftShopWholesaleTierData[]>([])
  const [data, setData] = stateData
  const theme = useTheme()
  const {lang} = useSelector('languageState')
  const [tierCount, setTierCount] = useState(0)
  const countAboveThreshold = useMemo(
    () => data.filter((x) => x.min_contents > 15).length,
    [data],
  )

  const handleCheckValidity = useCallback(
    async (max?: string) => {
      const lastIdx = data.length - 1
      const lastDisc = data[lastIdx].discount
      const prevDisc = data.length === 1 ? -1 : data[lastIdx - 1].discount

      if (lastDisc > GIFT_SHOP_WHOLESALE_MAX_DISCOUNT) {
        showSnackbar(translate('giftShop:wholesaleDiscountHigherMessage'))
        return false
      }
      if (isNaN(lastDisc) || lastDisc <= prevDisc) {
        showSnackbar(translate('giftShop:wholesaleDiscountLowerMessage'))
        return false
      }
      if (max) {
        const lastMax = parseInt(max, 10)
        const prevMax = data[lastIdx].min_contents - 1

        if (lastMax > GIFT_SHOP_WHOLESALE_MAX_CONTENT) {
          showSnackbar(translate('giftShop:wholesaleMaximumHigherMessage'))
          return false
        }
        if (lastMax <= prevMax) {
          showSnackbar(translate('giftShop:wholesaleMaximumLowerMessage'))
          return false
        }
      }

      return true
    },
    [data, translate],
  )

  const handleLoadData = useCallback(() => {
    setLoading(true)
    requestData('giftshop_get_creator_wholesale', {
      actionType: 'fetch',
      onRequestSuccess: ({status, data: {result}}) => {
        if (status === 200) {
          prevDatas.current = result.wholesale_tier
          setTierCount(result.wholesale_tier.length)
          setData(result.wholesale_tier)
          setLoading(false)
        }
      },
    })
  }, [setData])

  const handlePatchWholesale = useCallback(async () => {
    setButtonLoading(true)
    const valid = await handleCheckValidity()
    if (valid) {
      requestData('giftshop_patch_creator_wholesale', {
        actionType: 'execute',
        data: {wholesale_tier: data},
        onRequestSuccess: ({status}) => {
          setButtonLoading(false)
          if (status === 200) {
            setTierCount(data.length)
            showSnackbar(translate('giftShop:wholesaleChangesSuccess'))
          } else showSnackbar(translate('giftShop:wholesaleChangesFailed'))
        },
      })
    } else setButtonLoading(false)
  }, [data, handleCheckValidity, translate])

  const handleAddTier = useCallback(
    async (inputMax: string, inputDisc: string) => {
      const valid = await handleCheckValidity(inputMax)

      if (valid) {
        const {tier: prevTier, min_contents: prevMin} = data[data.length - 1]
        const intMax = parseInt(inputMax, 10)
        const intDisc = parseInt(inputDisc, 10)
        const newDisc =
          intDisc + GIFT_SHOP_WHOLESALE_DISCOUNT_INCREMENT >
          GIFT_SHOP_WHOLESALE_MAX_DISCOUNT
            ? GIFT_SHOP_WHOLESALE_MAX_DISCOUNT
            : intDisc + GIFT_SHOP_WHOLESALE_DISCOUNT_INCREMENT
        const newMin = inputMax.length === 0 ? prevMin + 1 : intMax + 1
        setData((prev) => [
          ...prev,
          {
            discount: newDisc,
            min_contents: newMin,
            tier: prevTier + 1,
          },
        ])
      }
    },
    [data, handleCheckValidity, setData],
  )

  const handleDeleteTier = useCallback(() => {
    setData((prev) => prev.slice(0, -1))
  }, [setData])

  const handleRenderDisclaimer = useMemo(
    () => (
      <StyledDisclaimerContainer>
        <Paragraph fontSize="s" color="gray_5">
          {translate('giftShop:settingWholesaleDisclaimer')}
        </Paragraph>
      </StyledDisclaimerContainer>
    ),
    [translate],
  )

  const handleRenderButton = useMemo(
    () => (
      <StyledButton
        label={translate('global:save')}
        onClick={handlePatchWholesale}
        disabled={!data || prevDatas.current === data}
        isLoading={buttonLoading}
      />
    ),
    [buttonLoading, data, handlePatchWholesale, translate],
  )

  const handleRenderRetry = useMemo(
    () => <ListFailed onRetry={handleLoadData} />,
    [handleLoadData],
  )

  const handleRenderHeader = useMemo(
    () => (
      <StyledHeader>
        <StyledLink
          href={`${getHowItWorksWebUrl()}?&lang=${lang}#creator-discount`}
          target="_blank">
          <StyledHeaderItem>
            <Icon size={14} type="book-question" color="primary_5" />
            <Paragraph fontWeight="bold" color="primary_5">
              {translate('global:howItWorks')}
            </Paragraph>
          </StyledHeaderItem>
        </StyledLink>
        <StyledHeaderItem
          disabled={tierCount === data.length}
          onClick={() =>
            tierCount === data.length ? undefined : handleLoadData()
          }>
          <Icon
            size={14}
            type="trash"
            color={tierCount === data.length ? 'gray_2' : 'primary_5'}
          />
          <Paragraph
            fontWeight="bold"
            color={tierCount === data.length ? 'gray_2' : 'primary_5'}>
            {translate('global:resetAll')}
          </Paragraph>
        </StyledHeaderItem>
      </StyledHeader>
    ),
    [data.length, handleLoadData, lang, tierCount, translate],
  )

  const handleRenderContents = useMemo(
    () =>
      data.map((item, idx) => (
        <GiftShopSettingWholesaleItem
          key={item.tier}
          data={item}
          dataLength={data.length}
          idx={idx}
          isLastTier={idx === data.length - 1}
          stateData={stateData}
          onAddTier={handleAddTier}
          onDeleteTier={handleDeleteTier}
        />
      )),
    [data, stateData, handleAddTier, handleDeleteTier],
  )

  const handleCalculatePriceSim = useCallback(
    (qty: number) => {
      let price = 0
      let amt = 0
      data.forEach((element) => {
        if (qty >= element.min_contents) {
          amt = element.discount
          price =
            qty *
            GIFT_SHOP_WHOLESALE_SIM_SAMPLE_PRICE *
            ((100 - element.discount) / 100)
        }
      })
      return (
        <tr>
          <td>
            <StyledSimCalc>{`${translate('global:buy')} ${qty}`}</StyledSimCalc>
          </td>
          <StyledTd>
            <StyledSimCalc>{`\u2192`}</StyledSimCalc>
          </StyledTd>
          <td>
            <StyledSimCalc>{`${translate(
              'global:discount',
            )} ${amt}%`}</StyledSimCalc>
          </td>
          <StyledTd>
            <StyledSimCalc>{`=`}</StyledSimCalc>
          </StyledTd>
          <td>
            <StyledSimCalcPrice>{`IDR ${formatCurrency(
              price,
            )}`}</StyledSimCalcPrice>
          </td>
        </tr>
      )
    },
    [data, translate],
  )

  const handleRenderPriceSim = useMemo(
    () => (
      <StyledSimContainer>
        <Paragraph fontSize="m">
          <ParsedText
            parse={[
              {
                pattern: /<b>.*?<\/b>/g,
                style: {fontFamily: getFontFamily('bold')},
                renderText: (text) => text.replace(/<b>|<\/b>/g, ''),
              },
            ]}>
            {translate('giftShop:wholeSaleSimPrice', {
              price: `${translate('global:idr')} ${formatCurrency(
                GIFT_SHOP_WHOLESALE_SIM_SAMPLE_PRICE,
              )}`,
            })}
          </ParsedText>
        </Paragraph>
        <StyledSimResult fontSize="m" fontWeight="medium">
          {translate('giftShop:wholeSaleSimSubtitle')}
        </StyledSimResult>
        <table>
          {Array.from({length: 15}).map((_, index) =>
            handleCalculatePriceSim(index + 1),
          )}
          {countAboveThreshold > 0 &&
            Array.from({
              length: countAboveThreshold,
            }).map((_, index) =>
              handleCalculatePriceSim(
                data[data.length - (countAboveThreshold - index)].min_contents,
              ),
            )}
        </table>
      </StyledSimContainer>
    ),
    [countAboveThreshold, data, handleCalculatePriceSim, translate],
  )

  useDidMount(handleLoadData)

  return (
    <StyledSection
      style={{
        borderRight: `1px solid ${theme.gray_1}`,
        ...containerStyle,
      }}>
      {showTitle && (
        <StyledSectionHeader>
          <Icon type="price-tags" />
          <Paragraph fontSize="l" fontWeight="medium">
            {translate('global:discount')}
          </Paragraph>
        </StyledSectionHeader>
      )}
      {loading ? (
        <StyledLoadingContainer>
          <StyledLottie animationData={animationData} />
        </StyledLoadingContainer>
      ) : data ? (
        <StyledContainer>
          {handleRenderHeader}
          {handleRenderContents}
          {handleRenderDisclaimer}
          {handleRenderButton}
          {handleRenderPriceSim}
        </StyledContainer>
      ) : (
        handleRenderRetry
      )}
    </StyledSection>
  )
}
